// ANGEL FLIGHT — PauseOverlay (DOM). RESUME / full settings / MAIN MENU.
//
// FLIR-styled modal overlay shown over the live game (the Router mounts it via
// showOverlay, leaving the Game host in place). A dimming scrim, a PAUSED title,
// RESUME (also bound to ESC), and the FULL settings panel the plumbing always
// supported but never surfaced: MASTER/SFX/MUSIC volume sliders (live —
// onSettingsChange re-applies to the AudioBus while you drag), the screenshake +
// hitstop feel toggles, a FLIR sensor toggle (flips RenderPipeline.setMode mid-
// mission), and the DIFFICULTY selector (applies on the NEXT deploy — the live
// run's stats are already resolved). Plus ABORT TO MENU. All persistence is
// delegated to the injected callbacks; navigation likewise.

import type { Screen } from '../Router';
import type { Difficulty, GameSettings } from '../../core/types';
import { DIFFICULTIES } from '../../config/difficulty';
import {
  FLIR,
  FONT,
  makeButton,
  makePanel,
  makeSegmented,
  makeSlider,
  makeToggle,
  makeTitle,
  el,
  type ButtonHandle,
  type SegmentedHandle,
  type SliderHandle,
  type ToggleHandle,
} from './ui';

export interface PauseOverlayOptions {
  /** Live settings to read the control states from. */
  settings: GameSettings;
  /** Current difficulty (changing it applies from the NEXT deploy). */
  difficulty: Difficulty;
  /** True while the FLIR (thermal) render mode is live. */
  flirOn: boolean;
  /** Resume the mission (close the overlay). */
  onResume: () => void;
  /** Abort to the main menu (tears down the mission). */
  onMainMenu: () => void;
  /** Called whenever a setting changes, with the full updated settings — the
   *  host persists it + re-applies (CameraRig feel, AudioBus volumes). */
  onSettingsChange: (settings: GameSettings) => void;
  /** Persist a difficulty change (next mission picks it up). */
  onDifficultyChange: (difficulty: Difficulty) => void;
  /** Toggle the FLIR render mode; returns the NEW state so the row stays true. */
  onFlirToggle: (on: boolean) => void;
}

export class PauseOverlay implements Screen<void> {
  private readonly opts: PauseOverlayOptions;
  private root: HTMLDivElement | null = null;
  private resumeBtn: ButtonHandle | null = null;
  private menuBtn: ButtonHandle | null = null;
  private shakeToggle: ToggleHandle | null = null;
  private hitstopToggle: ToggleHandle | null = null;
  private flirToggle: ToggleHandle | null = null;
  private sliders: SliderHandle[] = [];
  private diffSeg: SegmentedHandle | null = null;

  constructor(opts: PauseOverlayOptions) {
    this.opts = opts;
  }

  mount(parent: HTMLElement): void {
    const { settings } = this.opts;

    const root = el('div', {
      position: 'absolute',
      inset: '0',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '16px',
      background: 'rgba(2,4,6,0.66)',
      backdropFilter: 'blur(1px)',
      pointerEvents: 'auto',
      font: `600 14px/1.4 ${FONT}`,
      color: FLIR.green,
      overflowY: 'auto',
    });

    const title = makeTitle('PAUSED');

    this.resumeBtn = makeButton('RESUME', () => this.opts.onResume(), { accent: true });

    // ── AUDIO panel: the three volume buses, live while dragging ────────────
    const audioPanel = makePanel({
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
      width: 'min(380px, 86vw)',
    });
    audioPanel.append(this.panelLabel('AUDIO'));
    const mkVol = (label: string, key: 'master' | 'sfx' | 'music'): void => {
      const slider = makeSlider(label, settings[key], (v) => {
        settings[key] = v;
        this.opts.onSettingsChange(settings);
      });
      this.sliders.push(slider);
      audioPanel.append(slider.el);
    };
    mkVol('MASTER', 'master');
    mkVol('SFX', 'sfx');
    mkVol('MUSIC', 'music');

    // ── SENSOR / FEEL panel: FLIR + shake + hitstop ─────────────────────────
    const feelPanel = makePanel({
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
      width: 'min(380px, 86vw)',
    });
    feelPanel.append(this.panelLabel('SENSOR / FEEL'));
    this.flirToggle = makeToggle('FLIR THERMAL [T]', this.opts.flirOn, (on) => {
      this.opts.onFlirToggle(on);
    });
    this.shakeToggle = makeToggle('SCREENSHAKE', settings.screenshake, (on) => {
      settings.screenshake = on;
      this.opts.onSettingsChange(settings);
    });
    this.hitstopToggle = makeToggle('HITSTOP', settings.hitstop, (on) => {
      settings.hitstop = on;
      this.opts.onSettingsChange(settings);
    });
    feelPanel.append(this.flirToggle.el, this.shakeToggle.el, this.hitstopToggle.el);

    // ── DIFFICULTY panel (takes effect next deploy) ─────────────────────────
    const diffPanel = makePanel({
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
      width: 'min(380px, 86vw)',
    });
    diffPanel.append(this.panelLabel('DIFFICULTY — APPLIES NEXT DEPLOY'));
    this.diffSeg = makeSegmented(
      'TIER',
      Object.values(DIFFICULTIES).map((d) => ({ value: d.id, label: d.name.toUpperCase() })),
      this.opts.difficulty,
      (v) => this.opts.onDifficultyChange(v as Difficulty),
    );
    diffPanel.append(this.diffSeg.el);

    this.menuBtn = makeButton('ABORT TO MENU', () => this.opts.onMainMenu());

    root.append(title, this.resumeBtn.el, audioPanel, feelPanel, diffPanel, this.menuBtn.el);
    parent.appendChild(root);
    this.root = root;

    window.addEventListener('keydown', this.onKey);
  }

  private panelLabel(text: string): HTMLDivElement {
    return el(
      'div',
      {
        font: `700 12px/1 ${FONT}`,
        letterSpacing: '0.18em',
        color: FLIR.greenDim,
        marginBottom: '4px',
      },
      text,
    );
  }

  private readonly onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      this.opts.onResume();
    }
  };

  unmount(): void {
    window.removeEventListener('keydown', this.onKey);
    this.resumeBtn?.destroy();
    this.menuBtn?.destroy();
    this.shakeToggle?.destroy();
    this.hitstopToggle?.destroy();
    this.flirToggle?.destroy();
    for (const s of this.sliders) s.destroy();
    this.sliders = [];
    this.diffSeg?.destroy();
    this.resumeBtn = null;
    this.menuBtn = null;
    this.shakeToggle = null;
    this.hitstopToggle = null;
    this.flirToggle = null;
    this.diffSeg = null;
    this.root?.remove();
    this.root = null;
  }
}
